import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from '@/hooks/useToast';
import {
  CardCreditDTO,
  getListCardCreditsQueryKey,
  useUpdateCardCreditBySerial,
} from '@/api/trafikportalen';
import Button from '../core/Button';
import { IconCheck } from '@/icons';
import Input from '../core/Input';
import { FormElement } from '../FormElement';
import { OrganizationCreditInfo } from '../OrganizationCreditInfo';
import { FormSection } from '../FormSection';
import { formatCardNumber } from '@/helpers/formatting';
import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useOrgCreditInfo } from '@/hooks/useOrgCreditInfo';
import { TravellerCreditInfo } from '../TravellerCreditInfo';
import { TravellerWithCollectionMembershipsDTO } from '@/api/trafikportalen';
import { useTenantID } from '@/hooks/useTenantID';
import { getTenantCardCreditLimit } from '@/constants/tenantCardCreditLimits';

interface Props {
  card: CardCreditDTO;
  traveller: TravellerWithCollectionMembershipsDTO;
  onOpenChange: (open: boolean) => void;
}

export const CreditCardUpdateForm = ({
  card,
  traveller,
  onOpenChange,
}: Props) => {
  const { formatMessage, locale } = useIntl();
  const queryClient = useQueryClient();
  const updateCardCredit = useUpdateCardCreditBySerial();
  const { remainingCredit, hasNoCreditLimit } = useOrgCreditInfo();
  const tenantId = useTenantID();
  const tenantMaxCreditLimit = getTenantCardCreditLimit(tenantId);

  const [newCredit, setNewCredit] = useState(card.credit.toString());

  const creditError = useMemo(() => {
    if (!newCredit) {
      return '';
    }
    const credit = parseInt(newCredit);
    if (isNaN(credit)) {
      return formatMessage({ defaultMessage: 'Ogiltigt värde' });
    }
    if (credit < 0) {
      return formatMessage({
        defaultMessage: 'Kreditgränsen kan inte vara negativ',
      });
    }
    if (credit > tenantMaxCreditLimit) {
      return formatMessage(
        {
          defaultMessage: 'Kreditgränsen kan inte överstiga {maxLimit} SEK',
        },
        { maxLimit: tenantMaxCreditLimit.toLocaleString(locale) },
      );
    }
    if (!hasNoCreditLimit && credit > remainingCredit) {
      return formatMessage({
        defaultMessage:
          'Kreditgränsen kan inte överstiga organisationens kvarvarande kredit',
      });
    }
    return '';
  }, [
    newCredit,
    remainingCredit,
    formatMessage,
    hasNoCreditLimit,
    tenantMaxCreditLimit,
    locale,
  ]);

  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      updateCardCredit.mutate(
        {
          cardSerial: card.card_serial,
          data: {
            credit: Number(newCredit),
          },
        },
        {
          onSuccess: async () => {
            toast.success({
              title: formatMessage({
                defaultMessage: 'Kreditgräns uppdaterad',
              }),
            });

            await queryClient.invalidateQueries({
              queryKey: getListCardCreditsQueryKey(),
            });

            onOpenChange(false);
          },
          onError: (error) => {
            console.error('Error', error);
            toast.error({
              title: formatMessage({ defaultMessage: 'Ett fel inträffade' }),
            });
          },
        },
      );
    },
    [
      card.card_serial,
      formatMessage,
      newCredit,
      onOpenChange,
      queryClient,
      updateCardCredit,
    ],
  );

  return (
    <div className="flex flex-col gap-5">
      <OrganizationCreditInfo includeTitle={false} />
      <TravellerCreditInfo traveller={traveller} />
      <form className="flex flex-col gap-4" onSubmit={onSubmit}>
        <FormSection editMode={true}>
          <FormElement
            label={<FormattedMessage defaultMessage="Kortnummer" />}
            input={
              <span className="font-mono text-sm">
                {formatCardNumber(card.card_serial || '')}
              </span>
            }
          />
          <FormElement
            id="credit"
            label={<FormattedMessage defaultMessage="Ny kreditgräns" />}
            input={
              <Input
                id="credit"
                type="text"
                value={newCredit}
                onChange={(e) => setNewCredit(e.target.value)}
                endAdornment={<FormattedMessage defaultMessage="SEK" />}
                error={creditError}
                helperTextReserveSpace={true}
              />
            }
          />
        </FormSection>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            <FormattedMessage defaultMessage="Avbryt" />
          </Button>
          <Button
            type="submit"
            variant="filled"
            startIcon={<IconCheck />}
            disabled={!!creditError}
            loading={updateCardCredit.isPending}
          >
            <FormattedMessage defaultMessage="Uppdatera" />
          </Button>
        </div>
      </form>
    </div>
  );
};
